import {
  createEmptyCard,
  fsrs,
  generatorParameters,
  Rating,
  State,
  type Card,
  type FSRS,
  type Grade,
} from 'ts-fsrs';
import {
  cardsForSkill,
  getCard,
  logReview,
  putCard,
  type CardRecord,
} from './db';
import { cardKey, type CardKey, type Skill } from '../types';
import { getSpecies } from './region';

// The four grades the quiz UI can produce. We map a simple correct/incorrect
// answer onto Again/Good, and expose Hard/Easy for a future self-grade UI.
export { Rating, State };

let scheduler: FSRS | null = null;

function sched(): FSRS {
  if (!scheduler) {
    scheduler = fsrs(generatorParameters({ enable_fuzz: true }));
  }
  return scheduler;
}

/** Retrievability (0..1) of a card at `now`; 0 for brand-new cards. */
export function retrievability(card: Card, now: Date): number {
  if (card.state === State.New) return 0;
  return sched().get_retrievability(card, now, false);
}

/** Fetch an existing card record or synthesize a fresh (unsaved) one. */
export async function loadOrCreate(
  code: string,
  skill: Skill,
  now: Date,
): Promise<CardRecord> {
  const key = cardKey(code, skill);
  const existing = await getCard(key);
  if (existing) return existing;
  const card = createEmptyCard(now);
  return { key, code, skill, card, due: card.due };
}

/**
 * Grade a card and persist the new state + a review log entry.
 * Returns the updated record.
 *
 * `picked` is the species the learner named — the whole point of passing it is
 * the wrong ones, which are the only record the app keeps of what a bird gets
 * mistaken FOR (see `confusions` in core/progress.ts). Omitted where there is
 * no pick to record: the flip-card mode grades itself.
 */
export async function rate(
  code: string,
  skill: Skill,
  rating: Rating,
  now: Date = new Date(),
  picked?: string,
): Promise<CardRecord> {
  const rec = await loadOrCreate(code, skill, now);
  // `next` accepts a Grade (Rating minus Manual); our callers only pass grades.
  const { card } = sched().next(rec.card, now, rating as Grade);
  const updated: CardRecord = {
    key: rec.key,
    code,
    skill,
    card,
    due: card.due,
  };
  await putCard(updated);
  await logReview({
    key: rec.key,
    rating,
    correct: rating !== Rating.Again,
    // Undefined rather than an empty string: absent means "nothing was picked",
    // and every reader of the log tests for exactly that.
    ...(picked ? { picked } : {}),
    ts: now.getTime(),
  });
  return updated;
}

/** Convenience: grade from a boolean the quiz already computed. */
export function ratingFromCorrect(correct: boolean): Rating {
  return correct ? Rating.Good : Rating.Again;
}

/**
 * How commonly a species is seen in the region: 1 = most common. Species with
 * no ranking sort last rather than first, so a gap in the data can't push an
 * obscure bird to the front of a beginner's queue.
 */
function introductionRank(code: string): number {
  return getSpecies(code)?.rank ?? Number.MAX_SAFE_INTEGER;
}

/** The three tiers a queue is built from, each already in priority order. */
interface QueueTiers {
  /** Cards past their due date, most overdue first. */
  due: string[];
  /** Species this skill has never shown, commonest first. */
  fresh: string[];
  /** Cards not yet due, weakest memory first — the reservoir. */
  reservoir: string[];
}

/**
 * Sort the pool into the three tiers a study queue draws on.
 *
 * The reservoir is what keeps a session from ever coming back empty. Once a
 * learner has met every species in a skill and answered the day's due cards,
 * the first two tiers are both exhausted and the round used to render "Nothing
 * to study" — the better you got, the more often the app refused to be opened.
 * FSRS has an answer for this already: reviewing ahead of schedule is a normal,
 * supported move, and it costs the schedule only a slightly smaller interval
 * bump.
 *
 * Weakest-first is what makes the reservoir worth studying rather than just
 * non-empty. Retrievability is the model's estimate of "would you recall this
 * right now", so the card nearest to slipping is the card that surfaces —
 * the same ordering the due tier uses, continued past the due line.
 *
 * Everything is filtered to `allCodes` — the pool of species that actually have
 * the media this session needs. A due card outside the pool would be queued,
 * fail to build a question and be dropped, quietly shortening the round.
 */
async function queueTiers(
  skill: Skill,
  allCodes: string[],
  now: Date,
): Promise<QueueTiers> {
  const pool = new Set(allCodes);
  const cards = await cardsForSkill(skill);
  const inPool = cards.filter((r) => pool.has(r.code));
  const cutoff = now.getTime();

  const due = inPool
    .filter((r) => r.due.getTime() <= cutoff)
    .sort((a, b) => a.due.getTime() - b.due.getTime())
    .map((r) => r.code);

  // Introduce new species in order of how often they're actually encountered,
  // NOT in the order they appear in the manifest — that's taxonomic, so it opens
  // with a run of ducks and geese and a new learner meets a Mallard before a
  // robin. Display order and introduction order are different concerns.
  const seen = new Set(cards.map((r) => r.code));
  const fresh = allCodes
    .filter((c) => !seen.has(c))
    .sort((a, b) => introductionRank(a) - introductionRank(b));

  // Ties on retrievability (identical schedules, or the rare card the model
  // reads as fully retained) fall back to due date, so the reservoir still
  // drains in schedule order rather than in whatever order storage returned.
  const reservoir = inPool
    .filter((r) => r.due.getTime() > cutoff)
    .map((r) => ({ code: r.code, r: retrievability(r.card, now), due: r.due.getTime() }))
    .sort((a, b) => a.r - b.r || a.due - b.due)
    .map((x) => x.code);

  return { due, fresh, reservoir };
}

/**
 * The queue in strict priority order: everything due, then species never met,
 * then the reservoir, capped at `limit`.
 *
 * This is the "what most needs asking" ordering. The daily challenge takes the
 * front of it and so promises the bird gone longest without a review; a full
 * session wants a mix instead and calls `studySession`.
 */
export async function studyQueue(
  skill: Skill,
  allCodes: string[],
  now: Date,
  limit: number,
): Promise<string[]> {
  const { due, fresh, reservoir } = await queueTiers(skill, allCodes, now);
  return [...due, ...fresh, ...reservoir].slice(0, limit);
}

/**
 * Share of a session held for species the learner has never met, when nobody
 * says otherwise.
 *
 * Strict priority order spends every slot on due cards before it introduces
 * anything, and due cards are by definition the birds you keep getting wrong —
 * a failed card comes back within minutes, so ten shaky birds could own the
 * next several rounds outright and the region's other hundred-odd species were
 * unreachable until those ten went quiet. Holding three slots of ten keeps a
 * round mostly review, which is the point of a review app, while guaranteeing
 * the learner meets someone new every time they open one.
 *
 * It is the default rather than the rule: a setup screen can hold back none of
 * the round or all of it (core/quiz/settings.ts), because "three of ten" is a
 * guess about where someone is in their learning, and the learner working
 * through a region they have never birded and the one revising for a trip
 * tomorrow want opposite answers. This is what they get when they have not
 * said.
 */
export const NEW_SHARE = 0.3;

/**
 * One round's worth of species: mostly review, always some variety.
 *
 * Reserves `share` of the round for unmet species, gives the reservation
 * back to whichever tier can use it when the other runs short, tops up from the
 * reservoir, then weaves the new birds through the review ones so they arrive
 * spread across the round rather than bunched at the end.
 */
export async function studySession(
  skill: Skill,
  allCodes: string[],
  now: Date,
  limit: number,
  share: number = NEW_SHARE,
): Promise<string[]> {
  const { due, fresh, reservoir } = await queueTiers(skill, allCodes, now);

  // Each tier claims its share, then takes back whatever the other left unused:
  // no due cards means a round of nothing but new birds, and no new birds left
  // means the reservation quietly disappears. That second rule is what keeps a
  // share of 0 honest as "review first" rather than as "never introduce
  // anything": a learner with nothing due still meets new birds, because the
  // alternative is a round with no questions in it.
  const held = Math.min(fresh.length, Math.round(limit * share));
  const review = due.slice(0, limit - held);
  const meet = fresh.slice(0, limit - review.length);

  const round = weave(review, meet);
  for (const c of reservoir) {
    if (round.length >= limit) break;
    round.push(c);
  }
  return round;
}

/**
 * The rest of a round whose opening question has already been asked elsewhere.
 *
 * The daily challenge card on the home screen is a real question — it grades the
 * card and pays the XP — and it then hands off to the full session for that
 * skill. Built plainly, that session drew from the same queue the card just took
 * its bird off the front of, so the learner answered the same species twice in a
 * row and the round was ten questions on top of the one already done. Dropping
 * `done` from the pool and asking for one fewer makes the hand-off what it looks
 * like: the remaining nine of a round of ten.
 *
 * A `done` outside the pool changes nothing — a Sound ID card answered on a day
 * the species has no recording of the type this round wants, say.
 */
export async function studyRemainder(
  skill: Skill,
  allCodes: string[],
  now: Date,
  limit: number,
  done?: string,
  share: number = NEW_SHARE,
): Promise<string[]> {
  if (!done || !allCodes.includes(done)) return studySession(skill, allCodes, now, limit, share);
  return studySession(
    skill,
    allCodes.filter((c) => c !== done),
    now,
    limit - 1,
    share,
  );
}

/**
 * Merge `extra` into `main` at even spacing, keeping each list's own order.
 *
 * `main` always goes first when it has anything to give: a round opens on the
 * most overdue bird, never on an introduction, even in the lopsided case where
 * two cards are due and the rest of the round is new species.
 */
function weave(main: string[], extra: string[]): string[] {
  if (!main.length) return extra.slice();
  const rest = main.slice(1);
  const out = [main[0]];
  let i = 0;
  let j = 0;
  while (i < rest.length || j < extra.length) {
    const takeExtra =
      j < extra.length &&
      (i >= rest.length || (j + 0.5) / extra.length <= (i + 0.5) / rest.length);
    out.push(takeExtra ? extra[j++] : rest[i++]);
  }
  return out;
}

/** Map a card key back to its parts. */
export function parseKey(key: CardKey): { code: string; skill: Skill } {
  const idx = key.lastIndexOf(':');
  return {
    code: key.slice(0, idx),
    skill: key.slice(idx + 1) as Skill,
  };
}
